import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import AuthContext from "../Context/AuthContext";

// küçük debounce hook'u
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "U";
}

export default function NewMember() {
  const { userData, loggedIn } = useContext(AuthContext);

  const [name, setName] = useState("");
  const [eMail, setMail] = useState("");
  const [password, setPassword] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 200);

  const [loading, setLoading] = useState(false); // submit loading
  const [isFetching, setIsFetching] = useState(true); // accounts loading

  const [complete, setComplete] = useState(false);
  const [errorField, setErrorField] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // hesapları çek
  useEffect(() => {
    let mounted = true;
    async function fetchAccounts() {
      try {
        setIsFetching(true);
        const { data } = await axios.get("https://act-api.vercel.app/accounts");
        if (!mounted) return;
        setAccounts(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setErrorMsg("Hesap listesi yüklenemedi. Lütfen sayfayı yenileyin.");
      } finally {
        mounted && setIsFetching(false);
      }
    }
    fetchAccounts();
    return () => {
      mounted = false;
    };
  }, []);

  // arama filtresi
  const filteredAccounts = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const base = accounts || [];
    if (!term) return base.slice(0, 10); // boşken ilk 10 göster
    return base
      .filter(
        (a) =>
          (a?.name || "").toLowerCase().includes(term) ||
          (a?.company || "").toLowerCase().includes(term)
      )
      .slice(0, 20);
  }, [accounts, debouncedSearch]);

  function isSelected(acc) {
    return selectedAccounts.some((s) => s?._id === acc?._id);
  }

  function toggleAccount(acc) {
    if (isSelected(acc)) {
      setSelectedAccounts((prev) => prev.filter((s) => s._id !== acc._id));
    } else {
      setSelectedAccounts((prev) => [...prev, acc]);
    }
  }

  // doğrulama
  function validate() {
    if (!name.trim()) return "Name-Surname is required.";
    if (!eMail.trim()) return "Email is required.";
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(eMail.trim());
    if (!emailOk) return "Please enter a valid email address.";
    if (!password) return "Password is required.";
    if (password.length < 6) return "Password must be at least 6 characters.";
    return "";
  }

  async function addMember(e) {
    e.preventDefault();
    setComplete(false);
    setLoading(true);
    setErrorField("");
    setErrorMsg("");

    const Admin = userData?.user?.user?._id;
    if (!Admin) {
      setLoading(false);
      setErrorMsg("Oturum bilgileriniz bulunamadı. Lütfen tekrar giriş yapın.");
      return;
    }

    const v = validate();
    if (v) {
      setLoading(false);
      setErrorField(v);
      return;
    }

    try {
      const res = await axios.post("https://act-api.vercel.app/addamember", {
        name,
        eMail,
        password,
        selectedAccounts,
        Admin,
      });

      // 201/200 başarı
      if (res?.status === 201 || res?.status === 200) {
        setComplete(true);
        setName("");
        setMail("");
        setPassword("");
        setSelectedAccounts([]);
      } else {
        // Beklenmedik ama güvenli tarafta kal
        setErrorMsg("Beklenmeyen yanıt alındı. Lütfen tekrar deneyin.");
      }
    } catch (error) {
      const status = error?.response?.status;
      const payload = error?.response?.data;
      const msg = payload?.error || payload?.hata || "İşlem başarısız oldu.";

      if (status === 409) {
        // Duplicate e-mail
        setErrorField(msg || "Bu e-mail zaten kayıtlı.");
      } else if (status === 400) {
        // Eksik alan/validasyon hatası
        setErrorField(msg);
      } else {
        // Sunucu hatası vs.
        setErrorMsg(msg);
      }
    } finally {
      setLoading(false);
    }
  }


  if (loggedIn === false) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-slate-700 font-semibold text-lg">You are not logged in.</p>
          <p className="text-slate-500 mt-2">
            Go to{" "}
            <a href="/login" className="text-blue-600 underline">
              Login
            </a>{" "}
            page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Üst başlık */}
      <div className="sticky top-0 z-10 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold">Add Team Member</h1>
          <span className="text-sm text-slate-500">
            {selectedAccounts.length} account selected
          </span>
        </div>
      </div>

      {/* İçerik */}
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        {errorMsg && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {errorMsg}
          </div>
        )}
        {complete && (
          <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
            You added a team member.
          </div>
        )}
        {errorField && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-red-50 p-4 text-sm text-amber-800">
            {errorField}
          </div>
        )}

        <form
          onSubmit={addMember}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          {/* Temel bilgiler */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name-Surname <span className="text-red-500">*</span>
              </label>
              <input
                placeholder="Pick Your Name Surname"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                placeholder="Enter Your Email"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                value={eMail}
                onChange={(e) => setMail(e.target.value)}
                autoComplete="email"
                type="email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                placeholder="Enter Your Password"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                autoComplete="new-password"
              />
              <p className="mt-1 text-xs text-slate-500">
                En az 6 karakter olmalı.
              </p>
            </div>
          </div>

          {/* Hesap seçimi */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Accounts
            </label>

            <div className="relative">
              <input
                type="text"
                placeholder="Search accounts by name or company…"
                className="w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search accounts"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                />
              </svg>
            </div>

            {/* Yükleniyor iskeleti */}
            {isFetching && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-10 rounded-lg bg-slate-200 animate-pulse" />
                ))}
              </div>
            )}

            {/* Arama sonuçları listesi */}
            {!isFetching && (
              <ul className="mt-3 max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                {filteredAccounts.length === 0 ? (
                  <li className="p-3 text-sm text-slate-500">No accounts found.</li>
                ) : (
                  filteredAccounts.map((acc) => (
                    <li
                      key={acc._id}
                      className="p-3 text-sm hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                      onClick={() => toggleAccount(acc)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-slate-100 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center">
                          {acc.imageURL ? (
                            <img
                              src={acc.imageURL}
                              alt={acc.name}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement.textContent = initialsOf(acc.name);
                              }}
                            />
                          ) : (
                            <span className="text-xs text-slate-600 font-semibold">
                              {initialsOf(acc.name)}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">
                            {acc.name}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {acc.company || "—"}
                          </div>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        readOnly
                        checked={isSelected(acc)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </li>
                  ))
                )}
              </ul>
            )}

            {/* Seçilen hesap rozetleri */}
            {selectedAccounts.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedAccounts.map((acc) => (
                  <span
                    key={acc._id}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700"
                  >
                    {acc.name}
                    <button
                      type="button"
                      onClick={() => toggleAccount(acc)}
                      className="rounded-full p-0.5 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      aria-label={`Remove ${acc.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Aksiyonlar */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg
                    className="h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
